import { Chess } from "chess.js";
import { PgnMove } from "./PgnMove";
import { PgnParser } from "./PgnParser";
import { CHESS_START_POSITION } from "../constants";
export class PgnData {
    _fen;
    _pgn;
    _rootMove;
    _chess;
    _preGameComment;
    constructor(fen, pgn, rootMove, chess, preGameComment) {
        this._fen = fen;
        this._pgn = pgn;
        this._rootMove = rootMove;
        this._chess = chess;
        this._preGameComment = preGameComment;
    }
    static createEmpty(chess) {
        const chessInstance = chess || new Chess();
        chessInstance.reset(); // Ensure we start from initial position
        return new PgnData(CHESS_START_POSITION, "", null, chessInstance);
    }
    static createFromChessInstance(chess) {
        if (!chess) {
            throw new Error("Chess instance is required");
        }
        return new PgnData(chess.fen(), chess.pgn(), null, chess);
    }
    static create(pgn, chess, builder) {
        const fen = PgnParser.extractInitialFen(pgn);
        const { rootMove, preGameComment } = builder.parseMoves();
        return new PgnData(fen, pgn, rootMove, chess, preGameComment);
    }
    get fen() {
        return this._fen;
    }
    get pgn() {
        return this._pgn;
    }
    get preGameComment() {
        return this._preGameComment;
    }
    /**
     * Gets a move at the specified index in the main line
     * @param index The zero-based index of the move to get
     * @returns The move at the specified index, or null if not found
     */
    getMoveByIndex(index) {
        if (index < 0 || !this._rootMove) {
            return null;
        }
        let currentMove = this._rootMove;
        let currentIndex = 0;
        while (currentMove) {
            if (currentIndex === index) {
                return currentMove;
            }
            currentMove = currentMove.nextMove;
            currentIndex++;
        }
        return null;
    }
    /**
     * Returns an array of moves in the main line (excluding variations)
     */
    get moves() {
        if (!this._rootMove) {
            return [];
        }
        const moves = [];
        let currentMove = this._rootMove;
        while (currentMove) {
            moves.push(currentMove);
            currentMove = currentMove.nextMove;
        }
        return moves;
    }
    get rootMove() {
        return this._rootMove;
    }
    get rows() {
        return this.getRows();
    }
    getRows(options = {}) {
        return this._rootMove?.rows(options) || [];
    }
    get firstTurn() {
        return this._fen.split(" ")[1];
    }
    get isPuzzle() {
        return this.pgn.includes(PgnParser.REGEX.PUZZLE_MARKER);
    }
    /**
     * Builds a string representation of the moves in the PGN
     * @param move - The starting move (usually the root move)
     * @param options - Configuration options
     * @returns A string representation of the moves
     */
    buildMovesString(move, options = {}) {
        const { includeUserScore = true, stopAtMoveId, isPuzzleMove = false, } = options;
        if (!move)
            return "";
        // Stop if we've reached the specified move
        if (stopAtMoveId && move.id === stopAtMoveId) {
            return move.toString({ includeUserScore }) + " ";
        }
        let result = "";
        // Track if we've encountered a puzzle move
        let updatedIsPuzzleMove = isPuzzleMove;
        // if move is puzzle and isPuzzleMove is false, set isPuzzleMove to true and add puzzle annotation
        if (move.isPuzzleMove && !updatedIsPuzzleMove) {
            updatedIsPuzzleMove = true;
            result += PgnParser.REGEX.PUZZLE_MARKER + " ";
        }
        // Add move number if it's white's move or first move in a variation
        if (move.color === "w" || !move.previousMoveInline) {
            result += `${move.number}${move.color === "w" ? "." : "..."} `;
        }
        // Add the move itself using toString()
        result += move.toString({ includeUserScore }) + " ";
        // Don't process further moves if we've reached the stop move
        if (stopAtMoveId && move.id === stopAtMoveId) {
            return result;
        }
        if (move.childMoves && move.childMoves.length > 0) {
            for (const childMove of move.childMoves) {
                result +=
                    "(" +
                        this.buildMovesString(childMove, {
                            ...options,
                            isPuzzleMove: updatedIsPuzzleMove,
                        }).trim() +
                        ") ";
            }
        }
        // Continue with the next move in the same line
        if (move.nextMove) {
            result += this.buildMovesString(move.nextMove, {
                ...options,
                isPuzzleMove: updatedIsPuzzleMove,
            });
        }
        return result;
    }
    /**
     * Gets just the moves text without headers or pre-game comments
     */
    get movesString() {
        if (!this.rootMove)
            return "";
        return this.buildMovesString(this.rootMove, { isPuzzleMove: false }).trim();
    }
    /**
     * Gets a simplified header string without GameId, Setup, Homework, and empty values
     */
    get simplifiedHeader() {
        return PgnParser.getSimplifiedHeaders(this._pgn);
    }
    get userMoves() {
        // traverse through moves and childMoves
        const userMoves = [];
        const traverse = (move) => {
            if (!move)
                return;
            if (move.childMoves) {
                for (const childMove of move.childMoves) {
                    traverse(childMove);
                }
            }
            if (move.userScore != null) {
                userMoves.push(move);
            }
            traverse(move.nextMove);
        };
        traverse(this.rootMove);
        return userMoves;
    }
    /**
     * Gets the move for preview - returns the last user move if available, otherwise returns the last move for non-puzzles
     */
    get previewMove() {
        // If there are user moves, return the last user move
        if (this.userMoves.length > 0) {
            return this.userMoves[this.userMoves.length - 1];
        }
        if (this.isPuzzle) {
            return null;
        }
        // If the initial FEN is not the standard starting position, return the first move
        const headerFen = this.fen;
        if (headerFen !== CHESS_START_POSITION) {
            return this.rootMove;
        }
        // Otherwise, return the last move
        if (this.moves.length > 0) {
            return this.moves[this.moves.length - 1];
        }
        return null;
    }
    /**
     * Gets the FEN for preview - returns the FEN of the preview move if available, otherwise returns the initial FEN
     */
    get previewFen() {
        // If there are preview move, return the FEN of the last preview move
        if (this.previewMove) {
            return this.previewMove.fen;
        }
        // Otherwise, return the initial FEN
        return this.fen;
    }
    /**
     * Generates a PGN string representation of the chess game
     * @param {Object} options - Configuration options
     * @param {boolean} [options.includeUserScore=true] - Whether to include user scores in the output
     * @param {string} [options.stopAtMoveId] - If provided, stops generating moves after this move ID
     * @returns {string} The PGN string
     */
    toString(options = {}) {
        const normalizedHeader = PgnParser.normalizeHeaders(this._pgn) || "";
        // Generate moves section using the extracted method
        let movesText = "";
        if (this.rootMove) {
            movesText = this.buildMovesString(this.rootMove, options);
        }
        // Combine headers and moves
        return (normalizedHeader +
            "\n\n" +
            (this._preGameComment ? `{${this._preGameComment}} ` : "") +
            movesText.trim());
    }
    revealMove(moveId) {
        const move = this.rootMove?.findMoveById(moveId);
        if (move) {
            move.recordRevealed();
        }
    }
    addMove(san, previousMoveId) {
        const previousMove = previousMoveId
            ? this._rootMove?.findMoveById(previousMoveId)
            : null;
        // throw error if previousMoveId is not null and not found
        if (previousMoveId && !previousMove) {
            throw new Error(`Invalid previous move ID: ${previousMoveId}`);
        }
        const parentMove = previousMove ? previousMove.nextMove : this._rootMove;
        try {
            const newMove = new PgnMove(san, this._chess, previousMove, parentMove, this.fen);
            if (!newMove?.id) {
                throw new Error(`Invalid move: ${san} from ${previousMoveId}`);
            }
            if (!this._rootMove) {
                this._rootMove = newMove;
            }
            return newMove;
        }
        catch (error) {
            if (error.message.includes("Duplicate move:")) {
                // If it's a duplicate of parent move
                if (parentMove?.san === san) {
                    return parentMove;
                }
                // If it's a duplicate of a variation
                const existingMove = parentMove?.childMoves?.find((m) => m.san === san);
                if (existingMove) {
                    return existingMove;
                }
            }
            throw error;
        }
    }
}
